/**
 * Brand Color Theme Overrides
 *
 * Each brand color preset defines HSL overrides for the primary palette
 * in both light and dark modes.  When a company selects a brand color,
 * these values are injected as CSS custom-property overrides on the
 * CompanyLayout wrapper so that every Tailwind utility that references
 * `--primary`, `--ring`, or `--sidebar-primary` picks up the new hue.
 *
 * All values are bare HSL triples (e.g. "262 72% 35%") — they are
 * consumed by Tailwind's `hsl(var(--primary))` pattern.
 */

import { type CSSProperties } from 'react';
import { useTheme } from './components/ThemeProvider';

// ── HSL overrides per preset ─────────────────────────────────────────────────

interface BrandModeVars {
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  ring: string;
  sidebarPrimary: string;
  sidebarPrimaryForeground: string;
  sidebarAccent: string;
  sidebarAccentForeground: string;
  sidebarRing: string;
}

interface BrandColorDef {
  label: string;
  hex: string;
  light: BrandModeVars;
  dark: BrandModeVars;
}

export const BRAND_COLOR_PRESETS: BrandColorDef[] = [
  {
    label: 'Violet',
    hex: '#8b5cf6',
    light: {
      primary: '262 68% 34%',
      primaryForeground: '0 0% 100%',
      secondary: '262 60% 55%',
      secondaryForeground: '0 0% 100%',
      ring: '262 68% 34%',
      sidebarPrimary: '262 68% 34%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '262 40% 92%',
      sidebarAccentForeground: '262 68% 25%',
      sidebarRing: '262 68% 34%',
    },
    dark: {
      primary: '262 83% 66%',
      primaryForeground: '262 40% 8%',
      secondary: '262 50% 22%',
      secondaryForeground: '262 80% 85%',
      ring: '262 83% 66%',
      sidebarPrimary: '262 83% 66%',
      sidebarPrimaryForeground: '262 40% 8%',
      sidebarAccent: '262 45% 18%',
      sidebarAccentForeground: '262 80% 85%',
      sidebarRing: '262 83% 66%',
    },
  },
  {
    label: 'Indigo',
    hex: '#6366f1',
    light: {
      primary: '239 70% 36%',
      primaryForeground: '0 0% 100%',
      secondary: '239 60% 55%',
      secondaryForeground: '0 0% 100%',
      ring: '239 70% 36%',
      sidebarPrimary: '239 70% 36%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '239 40% 92%',
      sidebarAccentForeground: '239 70% 25%',
      sidebarRing: '239 70% 36%',
    },
    dark: {
      primary: '239 84% 67%',
      primaryForeground: '239 40% 8%',
      secondary: '239 50% 22%',
      secondaryForeground: '239 80% 85%',
      ring: '239 84% 67%',
      sidebarPrimary: '239 84% 67%',
      sidebarPrimaryForeground: '239 40% 8%',
      sidebarAccent: '239 45% 18%',
      sidebarAccentForeground: '239 80% 85%',
      sidebarRing: '239 84% 67%',
    },
  },
  {
    label: 'Blue',
    hex: '#3b82f6',
    light: {
      primary: '217 78% 34%',
      primaryForeground: '0 0% 100%',
      secondary: '217 70% 55%',
      secondaryForeground: '0 0% 100%',
      ring: '217 78% 34%',
      sidebarPrimary: '217 78% 34%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '217 45% 92%',
      sidebarAccentForeground: '217 78% 25%',
      sidebarRing: '217 78% 34%',
    },
    dark: {
      primary: '217 91% 60%',
      primaryForeground: '217 40% 8%',
      secondary: '217 50% 22%',
      secondaryForeground: '217 85% 85%',
      ring: '217 91% 60%',
      sidebarPrimary: '217 91% 60%',
      sidebarPrimaryForeground: '217 40% 8%',
      sidebarAccent: '217 45% 18%',
      sidebarAccentForeground: '217 85% 85%',
      sidebarRing: '217 91% 60%',
    },
  },
  {
    label: 'Cyan',
    hex: '#06b6d4',
    light: {
      primary: '189 90% 30%',
      primaryForeground: '0 0% 100%',
      secondary: '189 80% 48%',
      secondaryForeground: '0 0% 100%',
      ring: '189 90% 30%',
      sidebarPrimary: '189 90% 30%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '189 45% 92%',
      sidebarAccentForeground: '189 90% 22%',
      sidebarRing: '189 90% 30%',
    },
    dark: {
      primary: '189 95% 43%',
      primaryForeground: '189 40% 6%',
      secondary: '189 50% 20%',
      secondaryForeground: '189 85% 80%',
      ring: '189 95% 43%',
      sidebarPrimary: '189 95% 43%',
      sidebarPrimaryForeground: '189 40% 6%',
      sidebarAccent: '189 45% 16%',
      sidebarAccentForeground: '189 85% 80%',
      sidebarRing: '189 95% 43%',
    },
  },
  {
    label: 'Teal',
    hex: '#14b8a6',
    light: {
      primary: '173 75% 28%',
      primaryForeground: '0 0% 100%',
      secondary: '173 65% 45%',
      secondaryForeground: '0 0% 100%',
      ring: '173 75% 28%',
      sidebarPrimary: '173 75% 28%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '173 40% 92%',
      sidebarAccentForeground: '173 75% 20%',
      sidebarRing: '173 75% 28%',
    },
    dark: {
      primary: '173 80% 40%',
      primaryForeground: '173 40% 6%',
      secondary: '173 45% 20%',
      secondaryForeground: '173 75% 80%',
      ring: '173 80% 40%',
      sidebarPrimary: '173 80% 40%',
      sidebarPrimaryForeground: '173 40% 6%',
      sidebarAccent: '173 40% 16%',
      sidebarAccentForeground: '173 75% 80%',
      sidebarRing: '173 80% 40%',
    },
  },
  {
    label: 'Emerald',
    hex: '#10b981',
    light: {
      primary: '160 78% 27%',
      primaryForeground: '0 0% 100%',
      secondary: '160 68% 44%',
      secondaryForeground: '0 0% 100%',
      ring: '160 78% 27%',
      sidebarPrimary: '160 78% 27%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '160 40% 92%',
      sidebarAccentForeground: '160 78% 20%',
      sidebarRing: '160 78% 27%',
    },
    dark: {
      primary: '160 84% 39%',
      primaryForeground: '160 40% 6%',
      secondary: '160 45% 20%',
      secondaryForeground: '160 75% 80%',
      ring: '160 84% 39%',
      sidebarPrimary: '160 84% 39%',
      sidebarPrimaryForeground: '160 40% 6%',
      sidebarAccent: '160 40% 16%',
      sidebarAccentForeground: '160 75% 80%',
      sidebarRing: '160 84% 39%',
    },
  },
  {
    label: 'Amber',
    hex: '#f59e0b',
    light: {
      primary: '38 88% 36%',
      primaryForeground: '0 0% 100%',
      secondary: '38 80% 52%',
      secondaryForeground: '38 90% 10%',
      ring: '38 88% 36%',
      sidebarPrimary: '38 88% 36%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '38 50% 92%',
      sidebarAccentForeground: '38 88% 25%',
      sidebarRing: '38 88% 36%',
    },
    dark: {
      primary: '38 92% 50%',
      primaryForeground: '38 40% 8%',
      secondary: '38 50% 22%',
      secondaryForeground: '38 85% 80%',
      ring: '38 92% 50%',
      sidebarPrimary: '38 92% 50%',
      sidebarPrimaryForeground: '38 40% 8%',
      sidebarAccent: '38 45% 18%',
      sidebarAccentForeground: '38 85% 80%',
      sidebarRing: '38 92% 50%',
    },
  },
  {
    label: 'Orange',
    hex: '#f97316',
    light: {
      primary: '25 88% 36%',
      primaryForeground: '0 0% 100%',
      secondary: '25 80% 52%',
      secondaryForeground: '0 0% 100%',
      ring: '25 88% 36%',
      sidebarPrimary: '25 88% 36%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '25 50% 92%',
      sidebarAccentForeground: '25 88% 25%',
      sidebarRing: '25 88% 36%',
    },
    dark: {
      primary: '25 95% 53%',
      primaryForeground: '25 40% 8%',
      secondary: '25 50% 22%',
      secondaryForeground: '25 85% 80%',
      ring: '25 95% 53%',
      sidebarPrimary: '25 95% 53%',
      sidebarPrimaryForeground: '25 40% 8%',
      sidebarAccent: '25 45% 18%',
      sidebarAccentForeground: '25 85% 80%',
      sidebarRing: '25 95% 53%',
    },
  },
  {
    label: 'Rose',
    hex: '#f43f5e',
    light: {
      primary: '347 72% 38%',
      primaryForeground: '0 0% 100%',
      secondary: '347 65% 55%',
      secondaryForeground: '0 0% 100%',
      ring: '347 72% 38%',
      sidebarPrimary: '347 72% 38%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '347 40% 93%',
      sidebarAccentForeground: '347 72% 28%',
      sidebarRing: '347 72% 38%',
    },
    dark: {
      primary: '347 77% 60%',
      primaryForeground: '347 40% 8%',
      secondary: '347 45% 22%',
      secondaryForeground: '347 75% 82%',
      ring: '347 77% 60%',
      sidebarPrimary: '347 77% 60%',
      sidebarPrimaryForeground: '347 40% 8%',
      sidebarAccent: '347 40% 18%',
      sidebarAccentForeground: '347 75% 82%',
      sidebarRing: '347 77% 60%',
    },
  },
  {
    label: 'Fuchsia',
    hex: '#d946ef',
    light: {
      primary: '292 70% 36%',
      primaryForeground: '0 0% 100%',
      secondary: '292 60% 55%',
      secondaryForeground: '0 0% 100%',
      ring: '292 70% 36%',
      sidebarPrimary: '292 70% 36%',
      sidebarPrimaryForeground: '0 0% 100%',
      sidebarAccent: '292 35% 93%',
      sidebarAccentForeground: '292 70% 28%',
      sidebarRing: '292 70% 36%',
    },
    dark: {
      primary: '292 84% 61%',
      primaryForeground: '292 40% 8%',
      secondary: '292 45% 22%',
      secondaryForeground: '292 80% 82%',
      ring: '292 84% 61%',
      sidebarPrimary: '292 84% 61%',
      sidebarPrimaryForeground: '292 40% 8%',
      sidebarAccent: '292 40% 18%',
      sidebarAccentForeground: '292 80% 82%',
      sidebarRing: '292 84% 61%',
    },
  },
];

// Index by hex for fast lookup
const brandColorByHex = new Map(BRAND_COLOR_PRESETS.map((c) => [c.hex, c]));

/**
 * Resolve a hex string to a BrandColorDef, or undefined if not a known preset.
 */
export function getBrandColorDef(hex: string | undefined): BrandColorDef | undefined {
  if (!hex) return undefined;
  return brandColorByHex.get(hex);
}

/**
 * Build a CSSProperties object that overrides the primary/ring/sidebar tokens
 * for the current resolved theme (light or dark).
 *
 * Usage:
 * ```tsx
 * const brandVars = useBrandCSSVars(companyBrandColor);
 * <div style={brandVars}>…</div>
 * ```
 */
export function useBrandCSSVars(hex: string | undefined): CSSProperties | undefined {
  const { resolvedTheme } = useTheme();
  const def = getBrandColorDef(hex);
  if (!def) return undefined;

  const vars = resolvedTheme === 'dark' ? def.dark : def.light;

  return {
    '--primary': vars.primary,
    '--primary-foreground': vars.primaryForeground,
    '--secondary': vars.secondary,
    '--secondary-foreground': vars.secondaryForeground,
    '--ring': vars.ring,
    '--sidebar-primary': vars.sidebarPrimary,
    '--sidebar-primary-foreground': vars.sidebarPrimaryForeground,
    '--sidebar-accent': vars.sidebarAccent,
    '--sidebar-accent-foreground': vars.sidebarAccentForeground,
    '--sidebar-ring': vars.sidebarRing,
  } as CSSProperties;
}
